import { useEffect } from "react";
import { ArrowLeft, Check, Download, Package } from "lucide-react";
import { futuristicConcepts } from "./FuturisticWordmarks";

/* ════════════════════════════════════════════════════════════════
   6 LOGOMARK CONCEPTS — each is a self-contained SVG.
   All accept { size } in pixels. All use the PIXCAN spectrum palette.
   ════════════════════════════════════════════════════════════════ */

/* ① SPECTRUM X — the current mark. Two diagonal spectrum bars + dot matrix. */
const Mark1 = ({ size = 64 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="m1a" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ff6ee7" />
        <stop offset="50%" stopColor="#a88bff" />
        <stop offset="100%" stopColor="#5ef0ff" />
      </linearGradient>
      <linearGradient id="m1b" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a6ff4d" />
        <stop offset="60%" stopColor="#ffb347" />
        <stop offset="100%" stopColor="#ff6ee7" />
      </linearGradient>
    </defs>
    <path d="M10 6 L20 6 L38 42 L28 42 Z" fill="url(#m1a)" />
    <path d="M38 6 L28 6 L10 42 L20 42 Z" fill="url(#m1b)" />
    {[0, 1, 2, 3, 4].map((r) =>
      [0, 1, 2].map((c) => (
        <circle
          key={`${r}-${c}`}
          cx={3 + c * 3}
          cy={12 + r * 5}
          r="0.9"
          fill="#5ef0ff"
          opacity={0.15 + ((r + c) % 4) * 0.15}
        />
      ))
    )}
  </svg>
);

/* ② PHOTON APERTURE — converging light beams into a central photon node.
   Geometry: 8 spectrum rays converging to a luminous center. */
const Mark2 = ({ size = 64 }) => {
  const cx = 24,
    cy = 24,
    rOuter = 22,
    rInner = 5;
  const colors = [
    "#ff6ee7",
    "#c97bff",
    "#a88bff",
    "#7d9bff",
    "#5ef0ff",
    "#7df0c2",
    "#a6ff4d",
    "#ffb347",
  ];
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
      <defs>
        <radialGradient id="m2-core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="60%" stopColor="#5ef0ff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#5ef0ff" stopOpacity="0" />
        </radialGradient>
      </defs>
      {colors.map((c, i) => {
        const a = (i * Math.PI * 2) / colors.length - Math.PI / 2;
        const x1 = cx + Math.cos(a) * rOuter;
        const y1 = cy + Math.sin(a) * rOuter;
        const x2 = cx + Math.cos(a) * (rInner + 1.5);
        const y2 = cy + Math.sin(a) * (rInner + 1.5);
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={c}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        );
      })}
      <circle cx={cx} cy={cy} r="9" fill="url(#m2-core)" />
      <circle cx={cx} cy={cy} r="2.8" fill="#fff" />
    </svg>
  );
};

/* ③ PRISM SPLIT — a triangle splitting incoming white light into spectrum. */
const Mark3 = ({ size = 64 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="m3-tri" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#5ef0ff" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    {/* incoming white beam */}
    <line
      x1="2"
      y1="24"
      x2="18"
      y2="24"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* prism */}
    <path
      d="M18 8 L34 24 L18 40 Z"
      fill="url(#m3-tri)"
      stroke="rgba(255,255,255,0.6)"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    {/* dispersed spectrum rays */}
    {[
      { y: 13, color: "#ff6ee7" },
      { y: 18, color: "#a88bff" },
      { y: 23, color: "#5ef0ff" },
      { y: 28, color: "#a6ff4d" },
      { y: 33, color: "#ffb347" },
    ].map((r, i) => (
      <line
        key={i}
        x1="34"
        y1="24"
        x2="46"
        y2={r.y}
        stroke={r.color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    ))}
  </svg>
);

/* ④ INTERFERENCE — concentric rings showing wave interference. */
const Mark4 = ({ size = 64 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="m4-arc" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ff6ee7" />
        <stop offset="50%" stopColor="#a88bff" />
        <stop offset="100%" stopColor="#5ef0ff" />
      </linearGradient>
    </defs>
    {/* source A */}
    <circle cx="14" cy="24" r="2" fill="#ff6ee7" />
    {/* source B */}
    <circle cx="34" cy="24" r="2" fill="#5ef0ff" />
    {/* interference rings from A */}
    {[6, 11, 16, 21].map((r, i) => (
      <circle
        key={`a-${i}`}
        cx="14"
        cy="24"
        r={r}
        stroke="#ff6ee7"
        strokeOpacity={0.5 - i * 0.1}
        strokeWidth="1"
      />
    ))}
    {/* interference rings from B */}
    {[6, 11, 16, 21].map((r, i) => (
      <circle
        key={`b-${i}`}
        cx="34"
        cy="24"
        r={r}
        stroke="#5ef0ff"
        strokeOpacity={0.5 - i * 0.1}
        strokeWidth="1"
      />
    ))}
    {/* central bright fringe */}
    <ellipse
      cx="24"
      cy="24"
      rx="3"
      ry="10"
      fill="url(#m4-arc)"
      opacity="0.85"
    />
  </svg>
);

/* ⑤ VECTOR LATTICE — grid of nodes with one luminous photon traversing diagonally. */
const Mark5 = ({ size = 64 }) => {
  const grid = [];
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      grid.push({ r, c });
    }
  }
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
      <defs>
        <linearGradient id="m5-trace" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#ff6ee7" />
          <stop offset="50%" stopColor="#a88bff" />
          <stop offset="100%" stopColor="#5ef0ff" />
        </linearGradient>
      </defs>
      {/* lattice dots */}
      {grid.map(({ r, c }) => (
        <circle
          key={`${r}-${c}`}
          cx={8 + c * 8}
          cy={8 + r * 8}
          r="1.2"
          fill="#ffffff"
          opacity="0.22"
        />
      ))}
      {/* photon trace diagonal */}
      <line
        x1="8"
        y1="40"
        x2="40"
        y2="8"
        stroke="url(#m5-trace)"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      {/* leading photon */}
      <circle cx="40" cy="8" r="3" fill="#5ef0ff" />
      <circle cx="40" cy="8" r="6" fill="#5ef0ff" opacity="0.25" />
    </svg>
  );
};

/* ⑥ OPTICAL IRIS — concentric spectrum arcs forming an aperture/iris. */
const Mark6 = ({ size = 64 }) => {
  const arcs = [
    { color: "#ff6ee7", rotate: 0 },
    { color: "#a88bff", rotate: 60 },
    { color: "#5ef0ff", rotate: 120 },
    { color: "#a6ff4d", rotate: 180 },
    { color: "#ffb347", rotate: 240 },
    { color: "#ff6ee7", rotate: 300 },
  ];
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
      {arcs.map((a, i) => (
        <path
          key={i}
          d="M 24 6 A 18 18 0 0 1 42 24"
          stroke={a.color}
          strokeWidth="2.6"
          strokeLinecap="round"
          fill="none"
          transform={`rotate(${a.rotate} 24 24)`}
        />
      ))}
      <circle cx="24" cy="24" r="3.5" fill="#ffffff" />
    </svg>
  );
};

/* ════════════════════════════════════════════════════════════════
   Showcase page
   ════════════════════════════════════════════════════════════════ */

const concepts = [
  {
    id: 1,
    Mark: Mark1,
    name: "Spectrum X",
    file: "01_spectrum-x.svg",
    description:
      "Two diagonal spectrum bars forming an X with a dot-matrix accent. Direct evolution of your original wafer logo.",
    keywords: ["bold", "wafer-inspired", "asymmetric"],
  },
  {
    id: 2,
    Mark: Mark2,
    name: "Photon Aperture",
    file: "02_photon-aperture.svg",
    description:
      "Eight spectrum rays converging into a luminous photon core. Evokes light focusing into a single computation point.",
    keywords: ["radiant", "convergence", "iconic"],
  },
  {
    id: 3,
    Mark: Mark3,
    name: "Prism Split",
    file: "03_prism-split.svg",
    description:
      "A triangular prism dispersing a white beam into the visible spectrum. Pure photonic physics, instantly readable.",
    keywords: ["physics", "literal", "elegant"],
  },
  {
    id: 4,
    Mark: Mark4,
    name: "Interference",
    file: "04_interference.svg",
    description:
      "Two coherent sources creating an interference pattern — exactly how piXcan's chip computes similarity.",
    keywords: ["technical", "scientific", "precise"],
  },
  {
    id: 5,
    Mark: Mark5,
    name: "Vector Lattice",
    file: "05_vector-lattice.svg",
    description:
      "A photon traversing a grid of vector embeddings at the speed of light. Tells the retrieval story directly.",
    keywords: ["narrative", "data-rich", "modern"],
  },
  {
    id: 6,
    Mark: Mark6,
    name: "Optical Iris",
    file: "06_optical-iris.svg",
    description:
      "Six spectrum arcs forming a camera-iris-like aperture. Implies precision, focus, and a tech instrument.",
    keywords: ["geometric", "aperture", "instrumental"],
  },
];

const Concept = ({ id, Mark, name, file, description, keywords }) => (
  <div
    data-testid={`logo-concept-${id}`}
    className="glass rounded-2xl p-8 flex flex-col gap-6 reveal"
  >
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs text-[color:var(--ink-3)] tracking-widest uppercase">
        Concept {String(id).padStart(2, "0")}
      </span>
      <a
        href={`/logos/${file}`}
        download
        data-testid={`download-${id}`}
        className="font-mono text-[10px] text-[color:var(--cyan)] hover:text-white tracking-widest uppercase flex items-center gap-1.5 border border-[color:var(--cyan)]/30 hover:border-[color:var(--cyan)] rounded-full px-3 py-1 transition-colors"
      >
        <Download className="w-3 h-3" />
        SVG
      </a>
    </div>

    {/* Hero size on dark */}
    <div className="rounded-xl bg-[#050510] border border-white/5 h-44 flex items-center justify-center">
      <Mark size={120} />
    </div>

    {/* Three-size strip: light, mid, favicon */}
    <div className="grid grid-cols-3 gap-3">
      <div className="rounded-lg bg-white/95 border border-white/5 h-20 flex items-center justify-center">
        <Mark size={48} />
      </div>
      <div className="rounded-lg bg-[#0a0a18] border border-white/5 h-20 flex items-center justify-center">
        <Mark size={36} />
      </div>
      <div className="rounded-lg bg-[#10101f] border border-white/5 h-20 flex items-center justify-center">
        <Mark size={20} />
      </div>
    </div>

    {/* Lockup with wordmark */}
    <div className="rounded-xl bg-[#050510] border border-white/5 px-6 py-5 flex items-center gap-3">
      <Mark size={32} />
      <span className="font-display font-semibold text-white tracking-tight text-xl">
        <span>pi</span>
        <span className="spectrum-text" style={{ fontSize: "1.15em" }}>
          X
        </span>
        <span>can</span>
      </span>
    </div>

    <div>
      <h3 className="font-display text-2xl text-white mb-2">{name}</h3>
      <p className="text-[color:var(--ink-2)] text-sm leading-relaxed">
        {description}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {keywords.map((k) => (
          <span
            key={k}
            className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-2)] border border-white/10 rounded-full px-2.5 py-1"
          >
            {k}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const LogoShowcase = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="App min-h-screen" data-testid="logo-showcase-page">
      <div className="grain" />
      <div className="dot-matrix" style={{ opacity: 0.4 }} />

      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#050510]/70 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-4">
          <a
            href="/"
            data-testid="back-home"
            className="flex items-center gap-2 text-sm text-[color:var(--ink-2)] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
          <div className="font-mono text-xs text-[color:var(--ink-3)] tracking-widest uppercase">
            piXcan · Logo Concepts
          </div>
          <div className="w-20" />
        </div>
      </div>

      <main className="relative pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          {/* Heading */}
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase mb-4 reveal">
              Pick your favorite
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white leading-[1.05] reveal">
              Eighteen logo concepts,{" "}
              <span className="spectrum-text">one identity.</span>
            </h1>
            <p className="mt-6 text-lg text-[color:var(--ink-2)] leading-relaxed reveal">
              Each mark is hand-crafted SVG — infinitely scalable, animation-ready,
              and tuned to the photonic spectrum palette. Compare at hero, nav,
              and favicon sizes. Tell me the number and I'll wire it into the
              live site.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 reveal">
              <a
                href="/logos/pixcan-logos-all.zip"
                download
                data-testid="download-all-zip"
                className="btn-primary"
              >
                <Package className="w-4 h-4" />
                Download all (.zip)
              </a>
              <span className="font-mono text-xs text-[color:var(--ink-3)] tracking-widest uppercase">
                18 SVG files · 13 KB total
              </span>
            </div>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {concepts.map((c) => (
              <Concept key={c.id} {...c} />
            ))}
          </div>

          {/* ════════ FUTURISTIC WORDMARKS ════════ */}
          <div className="mt-32 mb-16">
            <div className="spectrum-line mb-16" />
            <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase mb-4 reveal">
              New direction · piXcan as one
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] reveal">
              Futuristic <span className="spectrum-text">wordmarks</span>.
            </h2>
            <p className="mt-6 text-lg text-[color:var(--ink-2)] leading-relaxed max-w-2xl reveal">
              Six full piXcan wordmarks where the X becomes the brand. Each
              treats the X with a distinct futuristic technique — pick the
              vibe that feels most you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {futuristicConcepts.map((c, i) => (
              <div
                key={c.id}
                data-testid={`wordmark-concept-${c.id}`}
                className="glass rounded-2xl p-8 flex flex-col gap-6 reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-[color:var(--ink-3)] tracking-widest uppercase">
                    Wordmark {String(c.id).padStart(2, "0")}
                  </span>
                  <a
                    href={`/logos/${c.file}`}
                    download
                    data-testid={`download-${c.id}`}
                    className="font-mono text-[10px] text-[color:var(--cyan)] hover:text-white tracking-widest uppercase flex items-center gap-1.5 border border-[color:var(--cyan)]/30 hover:border-[color:var(--cyan)] rounded-full px-3 py-1 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    SVG
                  </a>
                </div>

                <div className="rounded-xl bg-[#050510] border border-white/5 h-44 flex items-center justify-center px-6">
                  <c.Wm size={68} />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg bg-white/95 border border-white/5 h-20 flex items-center justify-center px-2">
                    <c.Wm size={28} />
                  </div>
                  <div className="rounded-lg bg-[#0a0a18] border border-white/5 h-20 flex items-center justify-center px-2">
                    <c.Wm size={32} />
                  </div>
                  <div className="rounded-lg bg-[#10101f] border border-white/5 h-20 flex items-center justify-center px-2">
                    <c.Wm size={20} />
                  </div>
                </div>

                <div>
                  <h3 className="font-display text-2xl text-white mb-2">
                    {c.name}
                  </h3>
                  <p className="text-[color:var(--ink-2)] text-sm leading-relaxed">
                    {c.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.keywords.map((k) => (
                      <span
                        key={k}
                        className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-2)] border border-white/10 rounded-full px-2.5 py-1"
                      >
                        {k}
                      </span>
                    ))}
                  </div>

                  {c.id === 16 && (
                    <div
                      data-testid="ascii-png-pack"
                      className="mt-6 pt-5 border-t border-white/10"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--cyan)]">
                          ★ Live brand · PNG pack
                        </span>
                        <a
                          href="/logos/png/16_ascii-glyph_all-PNGs.zip"
                          download
                          data-testid="ascii-png-zip"
                          className="font-mono text-[10px] text-[color:var(--cyan)] hover:text-white tracking-widest uppercase flex items-center gap-1.5 border border-[color:var(--cyan)]/40 hover:border-[color:var(--cyan)] rounded-full px-3 py-1 transition-colors"
                        >
                          <Package className="w-3 h-3" />
                          ZIP · 12 files
                        </a>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { f: "16_ascii-glyph_transparent_8k.png", l: "Transparent · 10K", sub: "10132×3328 · billboard" },
                          { f: "16_ascii-glyph_dark_8k.png", l: "Dark BG · 10K", sub: "10132×3328 · banner" },
                          { f: "16_ascii-glyph_transparent_4k.png", l: "Transparent · 4K", sub: "5066×1664 · poster" },
                          { f: "16_ascii-glyph_dark_4k.png", l: "Dark BG · 4K", sub: "5066×1664 · web hero" },
                          { f: "16_ascii-glyph_light_4k.png", l: "Light BG · 4K", sub: "5066×1664 · whitepaper" },
                          { f: "16_ascii-glyph_transparent_2k.png", l: "Transparent · 2K", sub: "2734×992 · slides" },
                          { f: "16_ascii-glyph_square_dark_2k.png", l: "Square · Dark", sub: "2000×2000 · LinkedIn" },
                          { f: "16_ascii-glyph_square_light_2k.png", l: "Square · Light", sub: "2000×2000" },
                          { f: "16_ascii-glyph_square_transparent_2k.png", l: "Square · Transparent", sub: "2000×2000" },
                          { f: "16_ascii-glyph_avatar_dark_2k.png", l: "Avatar · Dark", sub: "2000×2000 · profile pic" },
                          { f: "16_ascii-glyph_avatar_dark_1k.png", l: "Avatar · Dark", sub: "1000×1000 · twitter" },
                          { f: "16_ascii-glyph_avatar_transparent_2k.png", l: "Avatar · Transparent", sub: "2000×2000" },
                        ].map((p) => (
                          <a
                            key={p.f}
                            href={`/logos/png/${p.f}`}
                            download
                            data-testid={`png-${p.f}`}
                            className="group flex items-center justify-between gap-2 bg-white/[0.02] hover:bg-[color:var(--cyan)]/[0.08] border border-white/5 hover:border-[color:var(--cyan)]/40 rounded-lg px-3 py-2 transition-colors"
                          >
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-white truncate">
                                {p.l}
                              </div>
                              <div className="text-[10px] font-mono text-[color:var(--ink-3)] truncate">
                                {p.sub}
                              </div>
                            </div>
                            <Download className="w-3.5 h-3.5 text-[color:var(--ink-3)] group-hover:text-[color:var(--cyan)] flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* How to choose */}
          <div className="mt-20 glass rounded-2xl p-10 reveal">
            <div className="flex items-center gap-3 mb-4">
              <Check className="w-5 h-5 text-[color:var(--cyan)]" />
              <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--ink-2)]">
                How to choose
              </span>
            </div>
            <p className="text-[color:var(--ink-1)] text-lg leading-relaxed max-w-3xl">
              Reply with the concept number (e.g.,{" "}
              <span className="font-mono text-[color:var(--cyan)]">
                "go with #9"
              </span>
              ). Marks 01–06 are X-only logomarks; 07–12 are full piXcan
              wordmarks. I'll instantly apply your pick across the nav,
              footer, favicon, and OG share image.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LogoShowcase;
